import { AnyAction } from 'redux'

// Action Types
export const SET_FORM_FIELD = 'SET_FORM_FIELD'
export const RESET_FORM = 'RESET_FORM'
export const SET_FORM_ERRORS = 'SET_FORM_ERRORS'
export const CLEAR_FORM_ERROR = 'CLEAR_FORM_ERROR'
export const VALIDATE_FORM = 'VALIDATE_FORM'
export const SET_CARER_OPTIONS = 'SET_CARER_OPTIONS'

// Form State Interface
interface CarerOption {
  value: number | string // Support both number and string for compatibility
  label: string
}

interface FrequencyOption {
  value: string
  label: string
}

export interface SupervisionFormState {
  form: {
    name: string
    frequency: FrequencyOption | null
    carers: CarerOption[]
    startDate: string | null
    dueDate: string | null
  }
  errors: Record<string, string>
  carerOptions: CarerOption[]
  isLoading: boolean
  isValid: boolean
}

// Initial State
const initialState: SupervisionFormState = {
  form: {
    name: '',
    frequency: null,
    carers: [],
    startDate: null,
    dueDate: null
  },
  errors: {
    name: '',
    frequency: '',
    carers: '',
    startDate: '',
    dueDate: ''
  },
  carerOptions: [],
  isLoading: false,
  isValid: false
}

// Action Creators
export const setFormField = (field: string, value: unknown): { type: string; payload: { field: string; value: unknown } } => ({
  type: SET_FORM_FIELD,
  payload: { field, value },
})

export const resetForm = (): { type: string } => ({
  type: RESET_FORM,
})

export const setFormErrors = (errors: Record<string, string>): { type: string; payload: Record<string, string> } => ({
  type: SET_FORM_ERRORS,
  payload: errors,
})

export const clearFormError = (field: string): { type: string; payload: string } => ({
  type: CLEAR_FORM_ERROR,
  payload: field,
})

export const validateForm = (): { type: string } => ({
  type: VALIDATE_FORM,
})

export const setCarerOptions = (options: CarerOption[]): { type: string; payload: CarerOption[] } => ({
  type: SET_CARER_OPTIONS,
  payload: options,
})

export const setIsLoading = (isLoading: boolean): { type: string; payload: boolean } => ({
  type: 'SET_IS_LOADING',
  payload: isLoading,
})

// Validate form and return errors
const validateFormData = (form: SupervisionFormState['form']): { errors: Record<string, string>; isValid: boolean } => {
  const errors = {
    name: !form.name ? 'Name is required' : '',
    frequency: !form.frequency ? 'Frequency is required' : '',
    carers: !form.carers || form.carers.length === 0 ? 'Assigning users is required' : '',
    startDate: form.frequency?.value === 'ONE_TIME' && !form.startDate ? 'Available from date is required' : '',
    dueDate: form.frequency?.value === 'ONE_TIME' && !form.dueDate ? 'Deadline date is required' : ''
  }

  const isValid = !Object.values(errors).some(error => error)

  return { errors, isValid }
}

// Reducer
const supervisionFormReducer = (state = initialState, action: AnyAction): SupervisionFormState => {
  switch (action.type) {
    case SET_FORM_FIELD: {
      const { field, value } = action.payload
      return {
        ...state,
        form: {
          ...state.form,
          [field]: value
        },
      }
    }

    case RESET_FORM:
      return initialState

    case SET_FORM_ERRORS:
      return {
        ...state,
        errors: action.payload,
        isValid: !Object.values(action.payload).some(error => error),
      }

    case CLEAR_FORM_ERROR:
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.payload]: '',
        },
      }

    case VALIDATE_FORM: {
      const { errors, isValid } = validateFormData(state.form)
      return {
        ...state,
        errors,
        isValid,
      }
    }

    case SET_CARER_OPTIONS:
      return {
        ...state,
        carerOptions: action.payload,
      }

    case 'SET_IS_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      }

    default:
      return state
  }
}

export default supervisionFormReducer
